// src/components/circles/ContactCirclesSection.tsx
//
// Renders on the Contact detail slide-over: the Circles the contact
// belongs to, with add/remove actions and a click-through to each
// Circle's detail page. Data comes from the workspace circles list
// (which returns eager members) — no server-side "contact.circles"
// endpoint required.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetCirclesQuery,
  useAddCircleMembersMutation,
  useRemoveCircleMemberMutation,
} from '../../api/circlesApi';
import LoadingSpinner from '../common/LoadingSpinner';
import { useCircleDialogFocus } from './useCircleDialogFocus';
import CircleTypeBadge from './CircleTypeBadge';
import type { Circle } from '../../types/api';

interface ContactCirclesSectionProps {
  contactId: string;
  contactName: string;
  workspaceId: string;
}

export default function ContactCirclesSection({
  contactId,
  contactName,
  workspaceId,
}: ContactCirclesSectionProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const {
    data: circles = [],
    isLoading,
    error,
  } = useGetCirclesQuery({ workspaceId }, { skip: !workspaceId });
  const [removeMember, { isLoading: isRemoving }] = useRemoveCircleMemberMutation();

  // Smart Circles are not listed or offered here: their members are the
  // contacts matching their rules (evaluated server-side, per viewer), so
  // there is no stored row to show and nothing to add or remove by hand.
  const manualCircles = useMemo(() => circles.filter((c) => c.type !== 'smart_segment'), [circles]);
  const hasSmartCircles = manualCircles.length !== circles.length;

  // For each Circle in the workspace, find the row where this contact is
  // a member (if any). Preserves useful membership metadata for display.
  const memberships = useMemo(() => {
    const rows: Array<{
      circle: Circle;
      memberId: string;
      membershipStatus: string;
      membershipType: string;
      isPrimaryDepartment: boolean;
      startDate: string | null;
      endDate: string | null;
    }> = [];
    for (const circle of manualCircles) {
      const member = (circle.members ?? []).find((m) => m.contactId === contactId);
      if (member) {
        rows.push({
          circle,
          memberId: member.id,
          membershipStatus: member.membershipStatus,
          membershipType: member.membershipType,
          isPrimaryDepartment: member.isPrimaryDepartment,
          startDate: member.startDate,
          endDate: member.endDate,
        });
      }
    }
    return rows;
  }, [manualCircles, contactId]);

  const memberCircleIds = useMemo(
    () => new Set(memberships.map((row) => row.circle.id)),
    [memberships],
  );

  const availableCircles = useMemo(
    () => manualCircles.filter((c) => !c.archivedAt && !memberCircleIds.has(c.id)),
    [manualCircles, memberCircleIds],
  );

  return (
    <div data-testid="contact-circles-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-900">
          Circles ({memberships.length})
        </h4>
        {availableCircles.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setShowAdd(true);
              setBanner(null);
            }}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            data-testid="contact-add-to-circle-btn"
          >
            Add to circle
          </button>
        )}
      </div>

      {isLoading && (
        <div className="py-4 flex justify-center">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <p className="text-sm text-red-600">Failed to load circles.</p>
      )}

      {banner && (
        <div
          role="alert"
          className={`mb-2 rounded-lg border px-3 py-2 text-xs ${
            banner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {banner.message}
        </div>
      )}

      {!isLoading && memberships.length === 0 && (
        <p
          className="text-sm text-gray-500"
          data-testid="contact-circles-empty"
        >
          Not in any circles yet.
        </p>
      )}
      {!isLoading && hasSmartCircles && (
        <p className="mt-1 text-xs text-gray-500" data-testid="contact-circles-smart-note">
          Smart Circles aren’t listed here — their members come from rules.
        </p>
      )}

      {memberships.length > 0 && (
        <ul className="space-y-2">
          {memberships.map((row) => (
            <li
              key={row.circle.id}
              className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded-lg"
              data-testid={`contact-circle-row-${row.circle.id}`}
            >
              <div className="min-w-0">
                <Link
                  to={`/circles/${row.circle.id}`}
                  className="text-sm font-medium text-indigo-700 hover:underline truncate block"
                >
                  {row.circle.name}
                </Link>
                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-gray-500">
                  <CircleTypeBadge type={row.circle.type} />
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                    {row.membershipStatus}
                  </span>
                  {row.isPrimaryDepartment && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      Primary
                    </span>
                  )}
                  {row.startDate && <span>from {row.startDate}</span>}
                  {row.endDate && <span>until {row.endDate}</span>}
                </div>
              </div>
              <button
                type="button"
                disabled={isRemoving}
                onClick={async () => {
                  setBanner(null);
                  try {
                    await removeMember({
                      circleId: row.circle.id,
                      memberId: row.memberId,
                    }).unwrap();
                    setBanner({
                      kind: 'success',
                      message: `Removed ${contactName} from ${row.circle.name}.`,
                    });
                  } catch (err: any) {
                    setBanner({
                      kind: 'error',
                      message:
                        err?.data?.error ||
                        err?.data?.message ||
                        'Failed to remove from circle.',
                    });
                  }
                }}
                className="text-xs text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1"
                aria-label={`Remove ${contactName} from ${row.circle.name}`}
                data-testid={`contact-remove-from-circle-${row.circle.id}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <AddToCircleModal
          contactId={contactId}
          contactName={contactName}
          candidates={availableCircles}
          onClose={() => setShowAdd(false)}
          onDone={(circleName) => {
            setShowAdd(false);
            setBanner({
              kind: 'success',
              message: `Added ${contactName} to ${circleName}.`,
            });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add modal
// ---------------------------------------------------------------------------

function AddToCircleModal({
  contactId,
  contactName,
  candidates,
  onClose,
  onDone,
}: {
  contactId: string;
  contactName: string;
  candidates: Circle[];
  onClose: () => void;
  onDone: (circleName: string) => void;
}) {
  const [circleId, setCircleId] = useState<string>('');
  const [query, setQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addMembers, { isLoading }] = useAddCircleMembersMutation();
  const dialogRef = useCircleDialogFocus(onClose, isLoading);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return candidates.filter((c) =>
      needle ? c.name.toLowerCase().includes(needle) : true,
    );
  }, [candidates, query]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    const target = candidates.find((c) => c.id === circleId);
    if (!target) {
      setErrorMessage('Pick a circle.');
      return;
    }
    try {
      await addMembers({
        circleId: target.id,
        members: [{ contactId }],
      }).unwrap();
      onDone(target.name);
    } catch (err: any) {
      setErrorMessage(
        err?.data?.error || err?.data?.message || 'Failed to add to circle.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-single-title"
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-md focus:outline-none">
        <form onSubmit={handleSubmit}>
          <div className="p-5 border-b">
            <h2 id="add-single-title" className="text-lg font-semibold text-gray-900">
              Add {contactName} to a circle
            </h2>
          </div>
          <div className="p-5 space-y-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search circles..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Search circles"
            />
            <div
              className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto"
              role="listbox"
              aria-label="Choose circle"
            >
              {filtered.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">
                  {candidates.length === 0
                    ? 'This contact is already in every active circle.'
                    : 'No circles match your search.'}
                </p>
              ) : (
                filtered.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="radio"
                      name="target-circle-contact"
                      value={c.id}
                      checked={circleId === c.id}
                      onChange={() => setCircleId(c.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <CircleTypeBadge type={c.type} />
                    </div>
                  </label>
                ))
              )}
            </div>
            {errorMessage && (
              <p role="alert" className="text-sm text-red-600">
                {errorMessage}
              </p>
            )}
          </div>
          <div className="p-5 border-t flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !circleId}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
              data-testid="contact-add-single-submit"
            >
              {isLoading ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
